import { useState, useRef, useEffect, useMemo } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ComboboxEmployee {
  id: string | number;
  name: string;
  jobTitle?: string | null;
  department?: string | null;
}

interface EmployeeComboboxProps {
  employees: ComboboxEmployee[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Employees whose IDs should be excluded from the list */
  excludeIds?: Array<string | number>;
}

export function EmployeeCombobox({
  employees,
  value,
  onChange,
  placeholder = "Search employee...",
  disabled = false,
  className,
  excludeIds = [],
}: EmployeeComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const excluded = useMemo(() => excludeIds.map(String), [excludeIds]);

  const filtered = useMemo(
    () =>
      employees.filter((e) => {
        if (excluded.includes(String(e.id))) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
          e.name.toLowerCase().includes(q) ||
          (e.jobTitle ?? "").toLowerCase().includes(q) ||
          (e.department ?? "").toLowerCase().includes(q)
        );
      }),
    [employees, excluded, search],
  );

  const selected = employees.find((e) => String(e.id) === String(value));

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  function handleOpen() {
    if (disabled) return;
    setOpen(true);
    setSearch("");
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function handleSelect(emp: ComboboxEmployee) {
    onChange(String(emp.id));
    setOpen(false);
    setSearch("");
  }

  function handleClear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange("");
    setOpen(false);
    setSearch("");
  }

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      {/* Trigger */}
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        onClick={handleOpen}
        className={cn(
          "w-full rounded-xl border border-[#d0d5e8] h-14 bg-white dark:bg-card px-4 flex items-center justify-between text-sm transition-colors",
          "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
          !selected && "text-muted-foreground",
          disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <span className="truncate flex-1 text-left">
          {selected
            ? selected.name + (selected.jobTitle ? ` · ${selected.jobTitle}` : "")
            : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onPointerDown={handleClear}
              className="p-0.5 rounded hover:bg-muted"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          )}
          <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
        </span>
      </button>

      {/* Inline dropdown — no Popover/portal so no viewport/keyboard conflicts on mobile */}
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-popover border border-border rounded-xl shadow-lg">
          {/* Search input — font-size 16px prevents iOS Safari auto-zoom */}
          <div className="flex items-center border-b border-border px-3">
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={placeholder}
              className="flex-1 py-3 text-base bg-transparent outline-none placeholder:text-muted-foreground"
              style={{ fontSize: "16px" }}
            />
            {search && (
              <button type="button" onClick={() => setSearch("")} className="p-1">
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          {/* Scrollable list */}
          <div
            className="overflow-y-auto overscroll-contain rounded-b-xl"
            style={{ maxHeight: "220px", WebkitOverflowScrolling: "touch" } as React.CSSProperties}
          >
            {filtered.length === 0 ? (
              <p className="py-4 text-sm text-center text-muted-foreground">No employees found.</p>
            ) : (
              filtered.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => handleSelect(e)}
                  className={cn(
                    "w-full flex items-center gap-2 px-3 py-2.5 text-sm text-left hover:bg-accent transition-colors",
                    String(value) === String(e.id) && "bg-accent/60",
                  )}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0 text-primary",
                      String(value) === String(e.id) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  <span className="flex-1 truncate">
                    {e.name}
                    {(e.jobTitle || e.department) && (
                      <span className="text-muted-foreground ml-1.5 text-xs">
                        · {e.jobTitle || e.department}
                      </span>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
